import { inject } from '@angular/core';
import { Routes } from '@angular/router';
import { ngxPermissionsGuard } from 'ngx-permissions';
import { FeatureFlagService, LicenseService, RolePermissions } from 'shared/domain';
import {
  ClusterInvitationParams,
  ImportGuidelineParams,
  paramsToStateGuard,
  redirectBasedOnPermissions,
} from 'shared/util-common';

import { authGuard } from '@treetop/authentication';
import { OFFLINE_PAGE_ROUTES } from '@treetop/offline';

import { ADMIN_ROUTES } from './admin.routes';
import { licenseGuard } from '../../../../libs/shared/domain/src/lib/application/license.guard';
import { userGuard } from '../../../../libs/shared/domain/src/lib/application/user.guard';
import { isOnlineGuard } from '../../../../libs/shared/util-common/src/lib/guards/is-online.guard';

export const APP_ROUTES: Routes = [
  {
    path: '',
    loadComponent: () => import('./content/app-content.component').then((m) => m.AppContentComponent),
    canActivate: [isOnlineGuard, authGuard, userGuard, licenseGuard],
    children: [
      {
        path: '',
        canActivate: [redirectBasedOnPermissions],
        children: [],
      },
      {
        path: 'admin',
        children: ADMIN_ROUTES,
      },
      {
        path: 'smartbook',
        title: 'Treetop Dashboard',
        loadComponent: () => import('@treetop/smartbook').then((m) => m.SmartbookPageComponent),
        loadChildren: () => import('@treetop/smartbook').then((m) => m.SMARTBOOK_PAGE_ROUTES),
        canActivate: [ngxPermissionsGuard],
        data: {
          permissions: {
            only: [RolePermissions.viewDashboardPage],
            redirectTo: '/login',
          },
        },
      },
      {
        path: 'manage',
        title: 'Treetop Manage',
        loadChildren: () => import('@treetop/manage').then((m) => m.MANAGE_PAGE_ROUTES),
        canActivate: [
          ngxPermissionsGuard,
          () => inject(FeatureFlagService).editorEnabled(),
          () => inject(LicenseService).licensedFeatures().guidelineEditor,
        ],
        data: {
          permissions: {
            only: [RolePermissions.viewManagePage, RolePermissions.viewReviewPage, RolePermissions.editGuideline],
            redirectTo: '/login',
          },
        },
      },
      {
        path: 'share',
        title: 'Treetop Share',
        loadComponent: () => import('@treetop/share').then((m) => m.SharePageComponent),
        loadChildren: () => import('@treetop/share').then((m) => m.SHARE_PAGE_ROUTES),
        canActivate: [ngxPermissionsGuard],
        data: {
          permissions: {
            only: [RolePermissions.viewSharePage],
            redirectTo: '/login',
          },
        },
      },
      {
        path: 'guideline',
        loadChildren: () => import('@treetop/guideline').then((m) => m.GUIDELINE_PAGE_ROUTES),
      },
      {
        path: 'settings',
        loadChildren: () => import('@treetop/settings').then((m) => m.SETTINGS_PAGE_ROUTES),
      },
      {
        path: 'guidelines',
        title: 'Treetop Guidelines',
        loadComponent: () => import('@treetop/shared/guideline-coding').then((m) => m.GuidelineCodingPageComponent),
      },
      {
        path: 'statistics',
        title: 'Treetop Statistics',
        loadComponent: () => import('@treetop/statistics').then((m) => m.StatisticsPageComponent),
        canActivate: [ngxPermissionsGuard, () => inject(LicenseService).licensedFeatures().statistics],
        data: {
          permissions: {
            only: [RolePermissions.viewStatistics],
            redirectTo: '/settings',
          },
        },
      },
    ],
  },
  {
    path: 'home',
    pathMatch: 'full',
    redirectTo: '',
  },
  {
    path: 'import',
    canActivate: [
      paramsToStateGuard<ImportGuidelineParams>(
        ['uniqueId', 'source', 'guidelineName', 'guidelineOrganisationId'],
        ['/manage'],
        'importData',
      ),
    ],
    children: [],
  },
  {
    path: 'organization-invitation',
    canActivate: [paramsToStateGuard<ClusterInvitationParams>(['hash'], ['/share/cluster'], 'clusterInvitationData')],
    children: [],
  },
  {
    path: 'login',
    loadComponent: () => import('@treetop/authentication').then((m) => m.LoginComponent),
  },
  {
    path: 'offline',
    canActivate: [isOnlineGuard, userGuard],
    children: OFFLINE_PAGE_ROUTES,
  },
  { path: 'not-allowed', loadComponent: () => import('shared/ui-common').then((m) => m.PageNotAllowedComponent) },
  {
    path: 'logout',
    loadComponent: () => import('@treetop/authentication').then((m) => m.LogoutComponent),
  },
  { path: '**', loadComponent: () => import('shared/ui-common').then((m) => m.PageNotFoundComponent) },
];
